import React, { useState } from 'react'
import { useDidMountEffect } from '@zuto/utilities'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import { SpaceProps } from 'styled-system'
import Box from '@mui/material/Box'
import MuiErrorMessage from '@zuto/atoms/src/Mui/ErrorMessage/MuiErrorMessage'
import { SxProps } from '@mui/material'

export interface DateOfBirthValues {
  day?: string,
  month?: string,
  year?: string
}
export interface DateOfBirthProps extends
  Omit<React.HTMLAttributes<HTMLDivElement>, 'defaultValue'>, SpaceProps {
  error?: string
  defaultValue?: DateOfBirthValues
  valueChanged?: (_: DateOfBirthValues) => void
  onKeyPressHandler?: React.KeyboardEventHandler<HTMLInputElement>
  dobAutoFocus?: boolean
  variantOutlined?: boolean
  tooltipPresent?: boolean
  styleOverrides?: SxProps
}

const MuiDateOfBirth = ({
  error,
  defaultValue,
  valueChanged,
  onBlur,
  onKeyPressHandler,
  dobAutoFocus,
  styleOverrides
}: DateOfBirthProps): JSX.Element => {
  const [values, setValues] = useState<DateOfBirthValues>(defaultValue || {})

  const styles = {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap'
  }

  useDidMountEffect(() => {
    if (valueChanged) {
      valueChanged(values)
    }
  }, [values])

  const inputRefs: (HTMLInputElement | null)[] = []

  const sliceToMaxLength = (value: string, maxLength: string | null) => {
    if (!maxLength) {
      return value
    }

    return value.slice(0, parseInt(maxLength, 10))
  }

  const eventHandler = (input: HTMLInputElement): string => {
    let newValue = input.value.replace(/[^0-9]/g, '')
    newValue = sliceToMaxLength(newValue, input.getAttribute('maxLength'))

    return newValue
  }

  const autoFocusNext = (input: HTMLInputElement) => {
    const maxLength = input.getAttribute('maxlength')
    if (maxLength && input.value.length >= parseInt(maxLength, 10)) {
      const currentInputIndex = inputRefs.indexOf(input)
      if (currentInputIndex === inputRefs.length - 1) {
        return
      }
      const nextInput = inputRefs[currentInputIndex + 1]

      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.currentTarget.value = eventHandler(e.currentTarget)
    const { name } = e.currentTarget
    const { value } = e.currentTarget
    setValues((prev) => ({
      ...prev,
      [name]: value
    }))
    autoFocusNext(e.currentTarget)
  }

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.value = eventHandler(e.currentTarget)
    if (onBlur) onBlur(e)
  }

  return (
    <Box
      sx={{ ...styles }}
      data-hj-suppress=""
      onKeyPress={onKeyPressHandler}
    >
      <MuiTextField
        aria-label="label-day"
        autoComplete="bday-day"
        autoFocus={dobAutoFocus}
        data-hj-suppress=""
        defaultValue={defaultValue?.day}
        isError={!!error}
        id="day"
        inputMaxLength={2}
        inputSize="small"
        inputType="number"
        inputVariant="outlined"
        label="Day"
        max="31"
        min="1"
        name="day"
        onBlurHandler={onBlurHandler}
        onChangeHandler={onChangeHandler}
        pattern="[0-9]*"
        placeholder="DD"
        refHandler={(r: HTMLInputElement) => inputRefs.push(r)}
        spacingRight
        testId="day-input"
      />
      <MuiTextField
        aria-label="label-month"
        autoComplete="bday-month"
        data-hj-suppress=""
        defaultValue={defaultValue?.month}
        isError={!!error}
        id="month"
        inputMaxLength={2}
        inputSize="small"
        inputType="number"
        inputVariant="outlined"
        label="Month"
        max="12"
        min="1"
        name="month"
        onBlurHandler={onBlurHandler}
        onChangeHandler={onChangeHandler}
        pattern="[0-9]*"
        placeholder="MM"
        refHandler={(r: HTMLInputElement) => inputRefs.push(r)}
        spacingRight
        testId="month-input"
      />
      <MuiTextField
        aria-label="label-year"
        autoComplete="bday-year"
        data-hj-suppress=""
        defaultValue={defaultValue?.year}
        isError={!!error}
        id="year"
        inputMaxLength={4}
        inputSize="large"
        inputType="number"
        inputVariant="outlined"
        label="Year"
        min="1900"
        name="year"
        onBlurHandler={onBlurHandler}
        onChangeHandler={onChangeHandler}
        pattern="[0-9]*"
        placeholder="YYYY"
        refHandler={(r: HTMLInputElement) => inputRefs.push(r)}
        testId="year-input"
      />
      {error && (
        <MuiErrorMessage styleOverrides={styleOverrides} error={error} />
      )}
    </Box>
  )
}

export default MuiDateOfBirth
